import ParentEvaluationInfo from './components/ParentEvaluationInfo';
import {
  Col,
  DatePicker,
  Divider,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  ModalProps,
  Row,
  Select,
  SelectProps,
  Typography
} from 'antd';
import { IClassroomEvaluation } from 'interfaces/classrooms';
import { parseToDayjs, replaceTimeZone } from 'services/date';
import { useForm } from '@refinedev/antd';
import { useNotification, useOne, useUpdate } from '@refinedev/core';

export interface ReplacementEditModalProps {
  evaluationResource: string;
  modalClose: () => void;
  modalProps: ModalProps;
  assignmentSelectorProps: SelectProps<{ value: string; label: string }>;
  assesmentSelectorProps: SelectProps<{ value: string; label: string }>;
  parentId: string;
  evaluationId: string;
}

const ReplacementEditModal = ({
  evaluationResource,
  modalClose,
  modalProps,
  assignmentSelectorProps,
  assesmentSelectorProps,
  parentId,
  evaluationId
}: ReplacementEditModalProps) => {
  const { open } = useNotification();
  const { mutateAsync: updateEvaluation } = useUpdate();
  const { data: evaluationData } = useOne<IClassroomEvaluation>({
    resource: evaluationResource,
    id: parentId
  });
  const { form, formProps, queryResult } = useForm<IClassroomEvaluation>({
    resource: evaluationResource,
    id: evaluationId
  });
  const type = Form.useWatch('evaluation_type', form);

  const handleUpdateReplacement = async () => {
    return form
      .validateFields()
      .then(async () => {
        const [starts_at, ends_at] = form.getFieldValue('dateRange');
        return updateEvaluation({
          id: evaluationId,
          resource: evaluationResource,
          values: {
            ...form.getFieldsValue(),
            starts_at: replaceTimeZone(starts_at),
            ends_at: replaceTimeZone(ends_at),
            kind: 'replacement',
            passing_score: evaluationData?.data?.passing_score,
            parent_id: parentId
          }
        }).then(async () => {
          await queryResult
            ?.refetch()
            .then(
              response => (formProps.initialValues = { ...response.data?.data })
            );
          modalClose();
        });
      })
      .catch(() => {
        open?.({
          type: 'error',
          message: 'Favor conferir todos os campos obrigatórios.',
          description: 'Error ao criar registro!'
        });
      });
  };

  const handleSelectType = () => {
    form.setFieldsValue({ evaluation_id: null });
  };

  const handleInitialData = () => {
    return {
      ...queryResult?.data?.data,
      dateRange: [
        parseToDayjs(
          queryResult?.data?.data?.requirements?.[0]?.rules?.find(
            rule => rule.operator === 'after'
          )?.value
        ),
        parseToDayjs(
          queryResult?.data?.data?.requirements?.[0]?.rules?.find(
            rule => rule.operator === 'before'
          )?.value
        )
      ]
    };
  };

  return (
    <Modal
      {...modalProps}
      title="Editar avaliação substitutiva"
      cancelText="Cancelar"
      okText="Editar avaliação"
      className="!w-[1022px]"
      onOk={() => handleUpdateReplacement()}
      destroyOnClose
    >
      <Form
        form={form}
        {...formProps}
        initialValues={{
          ...handleInitialData()
        }}
        layout="vertical"
      >
        <ParentEvaluationInfo evaluationData={evaluationData?.data} />
        <Divider />
        <Typography.Title level={5}>
          Informações gerais da avaliação substitutiva
        </Typography.Title>

        <Row gutter={[16, 16]}>
          <Col md={8}>
            <Form.Item
              label="Tipo"
              name="evaluation_type"
              rules={[
                {
                  required: true
                }
              ]}
            >
              <Select onChange={handleSelectType}>
                <Select.Option value="Assessment">Avaliação</Select.Option>
                <Select.Option value="Assignment">Atividade</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col md={8}>
            <Form.Item
              label="Avaliação"
              name="evaluation_id"
              rules={[
                {
                  required: true
                }
              ]}
            >
              {type === 'Assessment' ? (
                <Select
                  notFoundContent={
                    <Empty description="Não há avaliações cadastradas para essa disciplina" />
                  }
                  disabled={!type}
                  {...assesmentSelectorProps}
                  style={{ minWidth: 200 }}
                />
              ) : (
                <Select
                  notFoundContent={
                    <Empty description="Não há atividades cadastradas para essa disciplina" />
                  }
                  disabled={!type}
                  {...assignmentSelectorProps}
                  style={{ minWidth: 200 }}
                />
              )}
            </Form.Item>
          </Col>
          <Col md={8}>
            <Form.Item
              label="Nome"
              name="name"
              rules={[
                {
                  required: true
                }
              ]}
            >
              <Input style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col md={8}>
            <Form.Item
              label="Tentativas"
              name="max_attempts"
              rules={[
                {
                  required: true
                }
              ]}
            >
              <InputNumber min={0} max={100} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        <Divider />
        <Row>
          <Typography.Title level={5}>Acesso</Typography.Title>
        </Row>
        <Row>
          <Typography.Paragraph>
            O período de acesso da avaliação deve está entre o inicio e fim da
            turma (dd/mm/aaaa - dd/mm/aaaa)
          </Typography.Paragraph>
        </Row>
        <Row>
          <Col md={20}>
            <Form.Item
              label="Acesso"
              name="dateRange"
              rules={[
                {
                  required: true
                }
              ]}
            >
              <DatePicker.RangePicker
                className="w-full"
                showTime
                placeholder={[
                  'Início (horário de Brasília)',
                  'Término (horário de Brasília)'
                ]}
                format="DD/MM/YYYY HH:mm"
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default ReplacementEditModal;
